import styles from './TabBar.module.css';
import type { TabKey } from '../../hooks/useTabRouter';
import { useMyTabs } from '../../hooks/useAdmin';

interface Props {
  activeTab: TabKey;
  onTabChange: (tab: TabKey) => void;
}

// Display labels — keys must stay in lockstep with backend _TAB_DEFINITIONS
// and the TAB_KEYS list in useTabRouter.
const ALL_TABS: { key: TabKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  // 'pipeline' = the native board (ex-`pipeline-v2`), promoted to `/` on 2026-08-11.
  { key: 'pipeline', label: 'Pipeline' },
  { key: 'staffing', label: 'Staffing' },
  { key: 'intel', label: 'Intel' },
  { key: 'sector-coverage', label: 'Sector Coverage' },
  { key: 'deals', label: 'Deal Updates' },
  { key: 'prompts', label: 'Prompts' },
  { key: 'pipeline-insights', label: 'Pipeline Insights' },
  { key: 'outreach', label: 'Outreach' },
  { key: 'vocabularies', label: 'Vocabularies' },
  { key: 'all-deals', label: 'All Deals' },
  { key: 'crm', label: 'CRM' },
  { key: 'nda', label: 'NDA' },
  // ⛔ Metrics, Pipeline by Sector, Agenda and the Affinity-fed board (`pipeline-v1`) were
  // RETIRED and deleted on 2026-09-01 (owner rulings, post-Affinity S1). Their keys are gone
  // from TAB_KEYS and from the backend's `_TAB_DEFINITIONS` too — a tab lives in three places,
  // and so does its deletion. Old links land on the default board via LEGACY_TAB_REDIRECTS.
  { key: 'settings', label: 'Settings' },
  { key: 'admin', label: 'Admin' },
];

export function TabBar({ activeTab, onTabChange }: Props) {
  const myTabs = useMyTabs();
  // With no server list, show all tabs; the server gates page rendering anyway. NOTE: App
  // no longer mounts this bar until `/api/tabs` has answered (owner report 2026-08-11 — the
  // chrome painted an over-generous bar and then snapped), so in the app this branch is now
  // the error path, not a loading state. Kept because the component must render something
  // sane on its own. Once the response lands we:
  //   - filter to the keys the server says we're allowed to see
  //   - render them in the SERVER ORDER (admin-configured via the
  //     drag-and-drop in Admin → Tab Visibility), NOT the static
  //     ALL_TABS order. ALL_TABS provides labels only.
  const labelByKey = new Map(ALL_TABS.map((t) => [t.key, t.label]));
  const tabs = myTabs.data
    ? myTabs.data.visible
        .filter((k) => labelByKey.has(k as TabKey))
        .map((k) => ({ key: k as TabKey, label: labelByKey.get(k as TabKey)! }))
    : ALL_TABS;

  return (
    <div className={styles.root}>
      {tabs.map((tab) => (
        <button
          key={tab.key}
          className={`${styles.tab} ${activeTab === tab.key ? styles.active : ''}`}
          onClick={() => onTabChange(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}
